import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, Smartphone, QrCode, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScannerActivation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [pinCode, setPinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [activated, setActivated] = useState(false);
  const [deviceToken, setDeviceToken] = useState('');
  const [eventId, setEventId] = useState('');
  
  const authId = searchParams.get('id');

  useEffect(() => {
    if (!authId) {
      toast.error('Link non valido');
      setTimeout(() => navigate('/'), 3000);
    }
  }, [authId]);

  const handleActivation = async () => {
    if (pinCode.length !== 6) {
      toast.error('Inserisci un codice a 6 cifre');
      return;
    }

    setLoading(true);

    try {
      // Get device info
      const deviceInfo = {
        name: `Scanner ${navigator.platform}`,
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language
      };

      const response = await axios.post(`${API_URL}/scanner/activate`, {
        auth_id: authId,
        pin_code: pinCode,
        device_info: deviceInfo
      });

      // Save token to localStorage
      localStorage.setItem('scanner_token', response.data.device_token);
      localStorage.setItem('scanner_event_id', response.data.event_id || '');
      localStorage.setItem('scanner_scope', response.data.scope || 'event');

      setDeviceToken(response.data.device_token);
      setEventId(response.data.event_id);
      setActivated(true);
      
      toast.success('🎉 Device autorizzato con successo!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice PIN non valido');
    } finally {
      setLoading(false);
    }
  };

  const handleInstallPWA = () => {
    toast.info('Per aggiungere alla schermata Home:\n\n📱 iPhone: Tap Share → "Aggiungi a Home"\n📱 Android: Menu → "Installa app"', {
      duration: 10000
    });
  };

  const goToScanner = () => {
    navigate('/scanner');
  };

  if (activated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-2xl">
          <CardHeader className="text-center bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-t-lg">
            <div className="flex justify-center mb-4">
              <CheckCircle className="w-20 h-20" />
            </div>
            <CardTitle className="text-2xl">✅ Device Autorizzato!</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-green-900 font-medium mb-2">
                Il tuo dispositivo è ora autorizzato per la scansione biglietti!
              </p>
              <p className="text-sm text-green-700">
                Token salvato in locale e pronto per l'uso
              </p>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={goToScanner}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                size="lg"
              >
                <QrCode className="w-5 h-5 mr-2" />
                Avvia Scanner
              </Button>

              <Button 
                onClick={handleInstallPWA}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <Download className="w-5 h-5 mr-2" />
                Aggiungi alla Schermata Home
              </Button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
                <Smartphone className="w-4 h-4 mr-2" />
                Come aggiungere alla Home
              </h3>
              <div className="text-sm text-blue-800 space-y-2">
                <p><strong>📱 iPhone/iPad:</strong></p>
                <ol className="list-decimal list-inside pl-2 space-y-1">
                  <li>Tap sul pulsante "Condividi" (icona con freccia in su)</li>
                  <li>Scorri e seleziona "Aggiungi a Home"</li>
                  <li>Conferma con "Aggiungi"</li>
                </ol>
                
                <p className="mt-3"><strong>📱 Android:</strong></p>
                <ol className="list-decimal list-inside pl-2 space-y-1">
                  <li>Tap sul menu (⋮) in alto a destra</li>
                  <li>Seleziona "Installa app" o "Aggiungi a Home"</li>
                  <li>Conferma l'installazione</li>
                </ol>
              </div>
            </div>

            <p className="text-xs text-center text-gray-500">
              Dopo aver aggiunto l'icona alla Home, potrai aprire lo scanner direttamente come un'app nativa!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-2xl">
        <CardHeader className="text-center bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-t-lg">
          <div className="flex justify-center mb-4">
            <Smartphone className="w-16 h-16" />
          </div>
          <CardTitle className="text-2xl">Attiva Scanner</CardTitle>
          <p className="text-blue-100 mt-2">Inserisci il codice PIN mostrato sul computer</p>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-sm text-blue-900">
                📱 Hai scansionato il QR code correttamente!
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Ora inserisci il codice a 6 cifre mostrato sul PC
              </p>
            </div>

            <div>
              <Label htmlFor="pin" className="text-lg font-semibold">
                Codice PIN (6 cifre)
              </Label>
              <Input
                id="pin"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={pinCode}
                onChange={(e) => setPinCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="text-center text-3xl font-mono tracking-widest mt-2 h-16"
                autoFocus
              />
              <p className="text-xs text-gray-500 text-center mt-2">
                Inserisci le 6 cifre del codice mostrato sul computer
              </p>
            </div>

            <Button 
              onClick={handleActivation}
              disabled={loading || pinCode.length !== 6}
              className="w-full h-12 text-lg"
            >
              {loading ? 'Attivazione...' : 'Attiva Device'}
            </Button>

            <div className="border-t pt-4">
              <p className="text-xs text-gray-500 text-center">
                Dopo l'attivazione, potrai usare questo dispositivo per scansionare i biglietti degli eventi autorizzati.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
